import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Building2 } from "lucide-react";

const BG = "https://static.prod-images.emergentagent.com/jobs/3314a7a0-c2a5-4cde-8293-8d2f5e2e9aac/images/f1cdfb79a96bf0bf7ef671ea377b70b9c507a6194b33f33ef2094e742a3621a2.png";

export default function RegisterSchool() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    accreditation_type: "state",
    accreditation_id: "",
    state: "",
    city: "",
    contact_email: "",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/schools", form);
      toast.success("School registered. Students can now sign up.");
      navigate("/signup");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[80vh]">
      <div className="relative hidden lg:block">
        <div
          className="absolute inset-0 opacity-60"
          style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">For Administrators</div>
          <div className="font-display text-3xl mt-3 tracking-tight">
            Onboard your school. Empower your creators.
          </div>
        </div>
      </div>
      <div className="px-6 md:px-12 py-16 max-w-xl">
        <div className="flex items-center gap-3 text-cyan8r">
          <Building2 size={20} />
          <div className="text-xs uppercase tracking-[0.3em]">School Registration</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Register a school</h1>
        <p className="text-zinc-400 mt-3">Provide your accreditation to unlock student sign-ups.</p>
        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <Label>School name</Label>
            <Input data-testid="school-name" value={form.name} onChange={upd("name")} required className="bg-[#0A0A0A] border-white/10 mt-1" />
          </div>
          <div>
            <Label>Accreditation type</Label>
            <RadioGroup
              value={form.accreditation_type}
              onValueChange={(v) => setForm({ ...form, accreditation_type: v })}
              className="grid grid-cols-2 gap-3 mt-2"
            >
              {["state", "regional"].map((t) => (
                <label key={t} className={`p-3 rounded-md border text-center cursor-pointer capitalize ${form.accreditation_type === t ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
                  <RadioGroupItem value={t} className="sr-only" />
                  {t}
                </label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label>Accreditation ID</Label>
            <Input data-testid="school-acrid" value={form.accreditation_id} onChange={upd("accreditation_id")} required className="bg-[#0A0A0A] border-white/10 mt-1 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>State</Label>
              <Input data-testid="school-state" value={form.state} onChange={upd("state")} required className="bg-[#0A0A0A] border-white/10 mt-1" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={upd("city")} className="bg-[#0A0A0A] border-white/10 mt-1" />
            </div>
          </div>
          <div>
            <Label>Contact email</Label>
            <Input type="email" value={form.contact_email} onChange={upd("contact_email")} className="bg-[#0A0A0A] border-white/10 mt-1" />
          </div>
          <Button data-testid="school-submit" type="submit" disabled={busy} className="w-full bg-white text-black hover:bg-zinc-200 h-11">
            {busy ? "Registering…" : "Register School"}
          </Button>
        </form>
      </div>
    </div>
  );
}
